/**
 * SphereGeometry - Isomorphe, deterministische Geodät-Sphären-Geometrie.
 *
 * Reine Funktionen ohne Browser- oder Server-Abhängigkeiten (kein import, kein
 * localStorage, keine Field-Klasse) — dadurch in Node UND im Browser lauffähig
 * und in Unit-Tests direkt prüfbar. Die erzeugte Topologie (Feld-IDs, Typ,
 * Nachbarn, normalisierte Zentren) ist die Single Source of Truth, die Server
 * und Client bit-identisch teilen müssen, damit Feld-Updates nicht auf falsche
 * Felder desyncen.
 *
 * Extrahiert aus der bisher in Server (Sphere.js) und Client (GameCore.js)
 * duplizierten Generierung. Der einzige client-/server-spezifische Unterschied
 * ist `fieldInset` (Rendering-Lücke zwischen Feldern): Client 0.92, Server 1.0.
 * Mit fieldInset=1 ergibt die Inset-Formel exakt die ungeschrumpften Vertices.
 *
 * @copyright reDim GmbH | @link https://www.redim.de | @contact team@redim.de
 */

/** Normalisiert einen Vektor auf Einheitslänge */
function normalize(v) {
    const len = Math.sqrt(v[0] * v[0] + v[1] * v[1] + v[2] * v[2]);
    return [v[0] / len, v[1] / len, v[2] / len];
}

/** Unterteilt alle Dreiecke einmal (Midpoint-Subdivision mit Kanten-Cache) */
function subdivide(vertices, faces) {
    const cache = {};
    const newFaces = [];

    const getMid = (i1, i2) => {
        const key = i1 < i2 ? `${i1}_${i2}` : `${i2}_${i1}`;
        if (cache[key] !== undefined) return cache[key];
        const v1 = vertices[i1];
        const v2 = vertices[i2];
        const mid = normalize([(v1[0] + v2[0]) / 2, (v1[1] + v2[1]) / 2, (v1[2] + v2[2]) / 2]);
        const idx = vertices.length;
        vertices.push(mid);
        cache[key] = idx;
        return idx;
    };

    for (const face of faces) {
        const a = getMid(face[0], face[1]);
        const b = getMid(face[1], face[2]);
        const c = getMid(face[2], face[0]);
        newFaces.push([face[0], a, c], [face[1], b, a], [face[2], c, b], [a, b, c]);
    }

    return { vertices, faces: newFaces };
}

/** Lloyd-Relaxation für gleichmäßigere Vertex-Verteilung */
function lloydRelaxation(vertices, faces, iterations) {
    let verts = vertices.map((v) => [...v]);

    for (let iter = 0; iter < iterations; iter++) {
        const neighbors = new Map();
        for (let i = 0; i < verts.length; i++) {
            neighbors.set(i, new Set());
        }
        // Insertion-Reihenfolge MUSS der Server-Version entsprechen: Set-Iteration
        // folgt der Insertion-Order, und die Reihenfolge beeinflusst die Float-
        // Akkumulation des Mittelwerts (ULP) — bei Grenzfällen kippt sonst die
        // Polygon-Sortierung und Server/Client-Geometrie driftet.
        for (const face of faces) {
            neighbors.get(face[0]).add(face[1]).add(face[2]);
            neighbors.get(face[1]).add(face[0]).add(face[2]);
            neighbors.get(face[2]).add(face[0]).add(face[1]);
        }

        verts = verts.map((v, i) => {
            const neighs = [...neighbors.get(i)];
            if (!neighs.length) return v;
            let cx = 0;
            let cy = 0;
            let cz = 0;
            for (const ni of neighs) {
                cx += verts[ni][0];
                cy += verts[ni][1];
                cz += verts[ni][2];
            }
            return normalize([cx / neighs.length, cy / neighs.length, cz / neighs.length]);
        });
    }

    return verts;
}

/**
 * Sortiert Punkte um einen Vertex (für Polygon-Reihenfolge / Winding-Order).
 * WICHTIG: Diese Version ist bit-identisch zur server-autoritativen Sphere.js —
 * `forward = right × up` (NICHT up × right) und `toC = c - vertex` bestimmen die
 * Winding-Order der gerenderten Polygone. Eine abweichende Reihenfolge kehrt die
 * Winding um und stellt die Sphäre gespiegelt dar (Steuerung/Kamera wirken vertauscht).
 */
function sortAroundVertex(vertex, centers) {
    const up = normalize(vertex);
    let ref = [0, 1, 0];
    if (Math.abs(up[0] * ref[0] + up[1] * ref[1] + up[2] * ref[2]) > 0.9) {
        ref = [1, 0, 0];
    }

    const right = normalize([
        up[1] * ref[2] - up[2] * ref[1],
        up[2] * ref[0] - up[0] * ref[2],
        up[0] * ref[1] - up[1] * ref[0]
    ]);

    const forward = [
        right[1] * up[2] - right[2] * up[1],
        right[2] * up[0] - right[0] * up[2],
        right[0] * up[1] - right[1] * up[0]
    ];

    const withAngles = centers.map((c) => {
        const toC = [c[0] - vertex[0], c[1] - vertex[1], c[2] - vertex[2]];
        const x = toC[0] * right[0] + toC[1] * right[1] + toC[2] * right[2];
        const y = toC[0] * forward[0] + toC[1] * forward[1] + toC[2] * forward[2];
        return { center: c, angle: Math.atan2(y, x) };
    });

    withAngles.sort((a, b) => a.angle - b.angle);
    return withAngles.map((w) => w.center);
}

/**
 * Erzeugt die vollständige Sphären-Geometrie als reine Daten.
 * @param {Object} opts
 * @param {number} opts.subdivisions - Unterteilungsgrad (1-5)
 * @param {number} [opts.lloydIterations=5] - Lloyd-Relaxations-Durchläufe
 * @param {number} [opts.radius=1] - Radius (skaliert center/vertices)
 * @param {number} [opts.fieldInset=1] - Feld-Verkleinerung fürs Rendering
 *   (1 = keine Lücke = Server-Geometrie, 0.92 = Client-Rendering mit Lücken)
 * @returns {Array<{id:number, type:string, centerNormalized:number[],
 *   center:number[], vertices:number[][], neighbors:number[], maxValue:number}>}
 *   Felder in stabiler ID-Reihenfolge (deterministisch, iterationsstabil).
 */
export function generateSphereGeometry({ subdivisions, lloydIterations = 5, radius = 1, fieldInset = 1 }) {
    // Ikosaeder-Basis
    const t = (1 + Math.sqrt(5)) / 2;
    let vertices = [
        [-1, t, 0],
        [1, t, 0],
        [-1, -t, 0],
        [1, -t, 0],
        [0, -1, t],
        [0, 1, t],
        [0, -1, -t],
        [0, 1, -t],
        [t, 0, -1],
        [t, 0, 1],
        [-t, 0, -1],
        [-t, 0, 1]
    ].map((v) => normalize(v));

    let faces = [
        [0, 11, 5],
        [0, 5, 1],
        [0, 1, 7],
        [0, 7, 10],
        [0, 10, 11],
        [1, 5, 9],
        [5, 11, 4],
        [11, 10, 2],
        [10, 7, 6],
        [7, 1, 8],
        [3, 9, 4],
        [3, 4, 2],
        [3, 2, 6],
        [3, 6, 8],
        [3, 8, 9],
        [4, 9, 5],
        [2, 4, 11],
        [6, 2, 10],
        [8, 6, 7],
        [9, 8, 1]
    ];

    for (let i = 0; i < subdivisions; i++) {
        const result = subdivide(vertices, faces);
        vertices = result.vertices;
        faces = result.faces;
    }

    vertices = lloydRelaxation(vertices, faces, lloydIterations);

    return createDualMeshData(vertices, faces, radius, fieldInset);
}

/**
 * Baut aus Vertices/Triangles die Feld-Daten (Dual-Mesh).
 * Interne Funktion — die Topologie (id/type/neighbors/centerNormalized) ist
 * identisch mit Server und Client; nur vertices/center hängen von radius/inset ab.
 */
function createDualMeshData(vertices, triangles, radius, inset) {
    const vertexTriangles = new Map();
    for (let i = 0; i < vertices.length; i++) {
        vertexTriangles.set(i, []);
    }
    for (let i = 0; i < triangles.length; i++) {
        for (const vi of triangles[i]) {
            vertexTriangles.get(vi).push(i);
        }
    }

    const centers = triangles.map((tri) => {
        const v0 = vertices[tri[0]];
        const v1 = vertices[tri[1]];
        const v2 = vertices[tri[2]];
        return normalize([
            (v0[0] + v1[0] + v2[0]) / 3,
            (v0[1] + v1[1] + v2[1]) / 3,
            (v0[2] + v1[2] + v2[2]) / 3
        ]);
    });

    const vertexToField = new Map();
    const vertexNeighbors = new Map();

    for (const tri of triangles) {
        const edges = [
            [tri[0], tri[1]],
            [tri[1], tri[2]],
            [tri[2], tri[0]]
        ];
        for (const [v1, v2] of edges) {
            if (!vertexNeighbors.has(v1)) vertexNeighbors.set(v1, new Set());
            if (!vertexNeighbors.has(v2)) vertexNeighbors.set(v2, new Set());
            vertexNeighbors.get(v1).add(v2);
            vertexNeighbors.get(v2).add(v1);
        }
    }

    const fieldsByVertex = new Map(); // vertexIdx → field-data
    const fields = [];
    let fieldId = 0;

    // Erster Durchlauf: Felder + Mapping
    for (const [vertexIdx, triIndices] of vertexTriangles) {
        if (triIndices.length < 5) continue;

        const vertex = vertices[vertexIdx];
        const faceCenters = triIndices.map((ti) => centers[ti]);
        const sorted = sortAroundVertex(vertex, faceCenters);

        const isPent = sorted.length === 5;
        const isHex = sorted.length === 6;
        if (!isPent && !isHex) continue;

        const center = normalize(vertex);
        const scaledCenter = center.map((c) => c * radius);

        // Polygon-Vertices mit Inset (inset=1 → ungeschrumpft = Server-Geometrie)
        const faceVertices = sorted.map((c) => {
            const scaled = c.map((v) => v * radius);
            return [
                scaledCenter[0] + (scaled[0] - scaledCenter[0]) * inset,
                scaledCenter[1] + (scaled[1] - scaledCenter[1]) * inset,
                scaledCenter[2] + (scaled[2] - scaledCenter[2]) * inset
            ];
        });

        const field = {
            id: fieldId,
            type: isPent ? 'pentagon' : 'hexagon',
            centerNormalized: center,
            center: scaledCenter,
            vertices: faceVertices,
            neighbors: []
        };

        vertexToField.set(vertexIdx, fieldId);
        fieldsByVertex.set(fieldId, { field, vertexIdx });
        fields.push(field);
        fieldId++;
    }

    // Zweiter Durchlauf: Nachbarn aus Topologie
    for (const { field, vertexIdx } of fieldsByVertex.values()) {
        const neighborVertices = vertexNeighbors.get(vertexIdx) || new Set();
        for (const neighborVertex of neighborVertices) {
            const neighborFieldId = vertexToField.get(neighborVertex);
            if (neighborFieldId !== undefined) {
                field.neighbors.push(neighborFieldId);
            }
        }
        field.maxValue = field.neighbors.length;
    }

    return fields;
}
